import {useCallback} from 'react';
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ArrowDownward,
    ArrowUpward,
    Delete,
    VerticalAlignBottom,
    VerticalAlignTop,
} from '@mui/icons-material';
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import CMemoryColorPicker
    from 'crqlar-react-common/dist/components/CMemoryColorPicker';
import {FabricObject} from 'fabric';
import {FloorPlanItem, RectCanvas, ShapeElement} from '../types';
import {FABRIC_DEFAULTS} from "../utils/fabric-defaults";
import {
    dashArrayToStrokeStyle,
    StrokeStyle,
    strokeStyleToDashArray
} from "../utils/fabric-factory";
import {useEditConfiguration} from "../hooks/useEditConfiguration";
import {useEditableTextField} from "../hooks/useEditableTextField";
import {useFloorplanContext} from "../hooks/useFloorplanContext";
import {useLiveScaledDimensions} from "../hooks/useLiveScaledDimensions";
import {
    getNormalizedShapeCanvas,
    getNormalizedShapeDimensions,
    isUniformShapeType,
} from "../utils/resize-utils";

interface Props {
    element: FabricObject;
    onBack: () => void;
    onElementUpdated: () => void;
}

export const EditShapeStyles: React.FC<Props> = ({
                                                     element,
                                                     onBack,
                                                     onElementUpdated,
                                                 }) => {
    const {t} = useTranslation();
    const {canvas} = useFloorplanContext();

    const item = element.floorPlanItem!;
    const elementData = item.element as ShapeElement;
    const elementCanvas = elementData.canvas;

    const {
        rebuild,
        position,
        handleUpdatePosition,
        rotation,
        handleRotationChange,
        updateSelectable,
        handleBringToFront,
        handleSendToBack,
        handleBringForward,
        handleSendBackward,
        handleDelete,
    } = useEditConfiguration({canvas, element, onBack, onElementUpdated});

    // Build updated item with canvas changes (applies scaling)
    const buildUpdatedItem = useCallback((updates: Record<string, any>): FloorPlanItem => {
        return {
            ...item,
            element: {
                ...elementData,
                canvas: {
                    ...getNormalizedShapeCanvas(elementData.canvas, elementData.shape, element.scaleX, element.scaleY),
                    ...updates,
                },
            },
        };
    }, [element.scaleX, element.scaleY, elementData, item]);

    const handleUpdate = useCallback((updates: Record<string, any>) => {
        rebuild(buildUpdatedItem(updates));
    }, [rebuild, buildUpdatedItem]);

    const getDimensions = useCallback((target: FabricObject) => {
        const targetCanvas = (target.floorPlanItem?.element as ShapeElement | undefined)?.canvas ?? elementCanvas;
        const targetShape = (target.floorPlanItem?.element as ShapeElement | undefined)?.shape ?? elementData.shape;
        return getNormalizedShapeDimensions(targetCanvas, targetShape, target.scaleX, target.scaleY);
    }, [elementCanvas, elementData.shape]);

    const scalingOverride = useLiveScaledDimensions(canvas, element, getDimensions);
    const dimensions = scalingOverride ?? getDimensions(element);
    const isUniformShape = isUniformShapeType(elementData.shape);

    // Update width/height, baking in any residual scale
    const handleSizeChange = useCallback((axis: 'width' | 'height', value: number) => {
        const normalizedCanvas = getNormalizedShapeCanvas(elementCanvas, elementData.shape, element.scaleX, element.scaleY);
        const updates: Record<string, any> = {[axis]: value};

        updates.height = axis === 'width' ? normalizedCanvas.height : value;
        updates.width = axis === 'height' ? normalizedCanvas.width : value;

        handleUpdate(updates);
    }, [element.scaleX, element.scaleY, elementCanvas, elementData.shape, handleUpdate]);

    const handleUniformSizeChange = useCallback((value: number) => {
        handleUpdate({width: value, height: value});
    }, [handleUpdate]);

    const handleSelectableChange = useCallback((selectable: boolean) => {
        updateSelectable(selectable);
        handleUpdate({selectable});
    }, [updateSelectable, handleUpdate]);

    const labelField = useEditableTextField({
        element,
        initialValue: elementCanvas.label ?? '',
        onSave: (value) => handleUpdate({label: value || undefined}),
        onUnmountSave: (value, elementId) => {
            const currentElement = elementId
                ? canvas?.getObjects().find(obj => obj.floorPlanItem?.id === elementId)
                : null;
            const data = currentElement?.floorPlanItem?.element as ShapeElement | undefined;
            if (data) {
                data.canvas.label = value || undefined;
            }
        },
    });

    const shapeLabel: Record<string, string> = {
        rect: t('Reference name for this rectangle'),
        ellipse: t('Reference name for this ellipse'),
        triangle: t('Reference name for this triangle'),
        hexagon: t('Reference name for this hexagon'),
        octagon: t('Reference name for this octagon'),
    };
    const labelHint = shapeLabel[elementData.shape] ?? t('Reference name for this shape');

    const isDisabled = elementCanvas?.selectable === false;
    const currentStrokeStyle = dashArrayToStrokeStyle(elementCanvas?.strokeDashArray as number[] | undefined);

    return (
        <Stack spacing={2.5}>
            <Box>
                <Tooltip title={labelHint} arrow>
                    <Typography variant="subtitle2" sx={{
                        mb: 0.5,
                        textDecoration: 'underline',
                        textDecorationStyle: 'dashed',
                        textUnderlineOffset: '3px',
                        cursor: 'help',
                        width: 'fit-content',
                    }}>{t('Label')}</Typography>
                </Tooltip>
                <TextField
                    size="small"
                    fullWidth
                    placeholder={t('Optional display name')}
                    value={labelField.value}
                    onChange={(e) => labelField.onChange(e.target.value)}
                    onBlur={labelField.onBlur}
                    onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                    disabled={isDisabled}
                    sx={{
                        '& .MuiInputBase-root': {
                            height: 40,
                        },
                    }}
                />
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            sx={{mb: 1.5}}>{t('Position')}</Typography>
                <Stack direction="row" spacing={1}>
                    <CNumberField
                        size="small"
                        label={t('Left')}
                        value={Math.round(position.x)}
                        onChange={(v: number | null) => v !== null && handleUpdatePosition(v, position.y)}
                        sx={{flex: 1}}
                        disabled={isDisabled}
                    />
                    <CNumberField
                        size="small"
                        label={t('Top')}
                        value={Math.round(position.y)}
                        onChange={(v: number | null) => v !== null && handleUpdatePosition(position.x, v)}
                        sx={{flex: 1}}
                        disabled={isDisabled}
                    />
                </Stack>
            </Box>

            {isUniformShape ? (
                <Box>
                    <Typography variant="subtitle2"
                                sx={{mb: 1.5}}>{t('Size')}</Typography>
                    <CNumberField
                        size="small"
                        label={elementData.shape === 'circle' ? t('Diameter') : t('Size')}
                        value={Math.round(dimensions.width)}
                        onChange={(v: number | null) => v !== null && v >= 10 && handleUniformSizeChange(v)}
                        min={10}
                        sx={{width: 'calc(50% - 4px)'}}
                        disabled={isDisabled}
                    />
                </Box>
            ) : (
                <Box>
                    <Typography variant="subtitle2"
                                sx={{mb: 1.5}}>{t('Size')}</Typography>
                    <Stack direction="row" spacing={1}>
                        <CNumberField
                            size="small"
                            label={t('Width')}
                            value={Math.round(dimensions.width)}
                            onChange={(v: number | null) => v !== null && v >= 10 && handleSizeChange('width', v)}
                            min={10}
                            sx={{flex: 1}}
                            disabled={isDisabled}
                        />
                        <CNumberField
                            size="small"
                            label={t('Height')}
                            value={Math.round(dimensions.height)}
                            onChange={(v: number | null) => v !== null && v >= 10 && handleSizeChange('height', v)}
                            min={10}
                            sx={{flex: 1}}
                            disabled={isDisabled}
                        />
                    </Stack>
                </Box>
            )}

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Rotation')}</Typography>
                <CNumberField
                    size="small"
                    fullWidth
                    value={Math.round(rotation)}
                    onChange={(v: number | null) => handleRotationChange(v ?? undefined)}
                    min={-360}
                    max={360}
                    disabled={isDisabled}
                />
            </Box>

            <Divider/>

            <Stack direction="row" spacing={1.5} sx={{opacity: isDisabled ? 0.5 : 1}}>
                <Box>
                    <Typography variant="subtitle2" gutterBottom>{t('Fill')}</Typography>
                    <CMemoryColorPicker
                        color={elementCanvas?.fill as string ?? FABRIC_DEFAULTS.shape.fill}
                        onChange={(color) => handleUpdate({fill: color})}
                        disabled={isDisabled}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle2" gutterBottom>{t('Stroke')}</Typography>
                    <CMemoryColorPicker
                        color={elementCanvas?.stroke as string ?? FABRIC_DEFAULTS.shape.stroke}
                        onChange={(color) => handleUpdate({stroke: color})}
                        disabled={isDisabled}
                    />
                </Box>
            </Stack>

            <Divider/>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Stroke Width')}</Typography>
                <CNumberField
                    size="small"
                    fullWidth
                    value={elementCanvas?.strokeWidth ?? FABRIC_DEFAULTS.shape.strokeWidth}
                    onChange={(v: number | null) => v !== null && v >= 0 && v <= 20 && handleUpdate({strokeWidth: v})}
                    min={0}
                    max={20}
                    disabled={isDisabled}
                />
            </Box>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Stroke Style')}</Typography>
                <Select
                    size="small"
                    fullWidth
                    value={currentStrokeStyle}
                    onChange={(e) => handleUpdate({strokeDashArray: strokeStyleToDashArray(e.target.value as StrokeStyle)})}
                    disabled={isDisabled}
                >
                    <MenuItem value="solid">{t('Solid')}</MenuItem>
                    <MenuItem value="dashed">{t('Dashed')}</MenuItem>
                    <MenuItem value="dotted">{t('Dotted')}</MenuItem>
                    <MenuItem value="long-dashed">{t('Long Dashed')}</MenuItem>
                </Select>
            </Box>

            {elementData.shape === 'rect' && (
                <Box>
                    <Typography variant="subtitle2"
                                gutterBottom>{t('Corner Radius')}</Typography>
                    <CNumberField
                        size="small"
                        fullWidth
                        value={(elementCanvas as RectCanvas).rx ?? FABRIC_DEFAULTS.shape.cornerRadius}
                        onChange={(v: number | null) => v !== null && v >= 0 && v <= 50 && handleUpdate({
                            rx: v,
                            ry: v
                        })}
                        min={0}
                        max={50}
                        disabled={isDisabled}
                    />
                </Box>
            )}

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Interaction')}</Typography>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={elementCanvas?.selectable !== false}
                            onChange={(e) => handleSelectableChange(e.target.checked)}
                        />
                    }
                    label={<Typography
                        variant="body2">{t('Selectable on canvas')}</Typography>}
                />
            </Box>

            <Divider/>

            <Box>
                <Typography variant="subtitle2"
                            gutterBottom>{t('Layer')}</Typography>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('Bring to Front')}>
                        <span>
                            <IconButton size="small" onClick={handleBringToFront}
                                        disabled={isDisabled}>
                                <VerticalAlignTop fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Bring Forward')}>
                        <span>
                            <IconButton size="small" onClick={handleBringForward}
                                        disabled={isDisabled}>
                                <ArrowUpward fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Send Backward')}>
                        <span>
                            <IconButton size="small" onClick={handleSendBackward}
                                        disabled={isDisabled}>
                                <ArrowDownward fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Send to Back')}>
                        <span>
                            <IconButton size="small" onClick={handleSendToBack}
                                        disabled={isDisabled}>
                                <VerticalAlignBottom fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Box>

            <Divider/>

            <Box>
                <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<Delete/>}
                    onClick={handleDelete}
                >
                    {t('Delete Shape')}
                </Button>
            </Box>
        </Stack>
    );
};
